/**
 * welcomeMat.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import OT from "@opentok/client";
import * as React from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
//import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
//import LoginIcon from "@mui/icons-material/Login";
//import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
//import MenuList from "@mui/material/MenuList";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
//import MoreVertIcon from "@mui/icons-material/MoreVert";
//import Paper from "@mui/material/Paper";
//import Popper from "@mui/material/Popper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { Membership } from "../models/auth";
import { GroupMembership, GroupType } from "../models/group";
import { authServiceFactory } from "../services/auth.service";
import { conferenceServiceFactory } from "../services/conferencing.service";
import { groupServiceFactory } from "../services/group.service";

interface Props {
  groupMembership?: GroupMembership,
  sendVideo: boolean,
  sendAudio: boolean,
  hideSessionOptions: boolean,
  proceedText?: string,
  isWaiting: boolean,
  meetingHasStarted: boolean,
  onToggleVideo?: () => void,
  onToggleAudio?: () => void,
  onJoin: (isRouted: boolean, notifyMembers: Array<Membership> | undefined,
      videoSrcID: string, audioSrcID: string) => void,
  onClose?: () => void
}

export function WelcomeMatComponent(props: Props): React.ReactElement<any, any> {
  const [publisher, setPublisher] = React.useState<OT.Publisher | null>(null);
  const [audioDevices, setAudioDevices] = React.useState<Array<OT.Device>>([]);
  const [videoDevices, setVideoDevices] = React.useState<Array<OT.Device>>([]);
  const [audioSrcID, setAudioSource] = React.useState<string>("");
  const [videoSrcID, setVideoSource] = React.useState<string>("");
  //const [openMenu, setOpenMenu] = React.useState<boolean>(false);
  //const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isRouted, setIsRouted] = React.useState<boolean>(true);
  const [memberSearchVal, setMemberSearchVal] = React.useState<string>("");
  const [groupMemberSet, setGroupMemberSet] = React.useState<Set<string>>(new Set());
  const [members, setMembers] = React.useState<Array<Membership>>([]);
  const [selectedMembers, setSelectedMembers] = React.useState<Array<Membership>>([]);
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [errMsg, setErrMsg] = React.useState<string>("");

  const isDM = props.groupMembership?.grouptype === GroupType.dm;

  publisher?.publishAudio(props.sendAudio);
  publisher?.publishVideo(props.sendVideo);
  if (audioSrcID) {
    publisher?.setAudioSource(audioSrcID);
  }
  if (videoSrcID) {
    publisher?.setVideoSource(videoSrcID);
  }

  React.useEffect(function () {
    const conferenceService = conferenceServiceFactory();
    let pub: OT.Publisher | null = null;
    let closing: boolean = false;

    // Publisher first because it blocks until the user gives permission
    conferenceService.createPublisher("publisher", "", true, true).then(publisher => {
      pub = publisher;
      if (closing) {
        pub.destroy();
        return Promise.reject("Welcome mat closed by user");
      }
      setPublisher(publisher);
      publisher.on("destroyed", () => {
        setPublisher(null);
      });
      return conferenceService.getMediaDevices();
    }).then(([videoDevices, audioDevices]) => {
      setVideoDevices(videoDevices);
      setAudioDevices(audioDevices);
    }, err => {
      console.error("Error setting up welcome mat:", err);
      const anyerr = err as any;
      if (anyerr.originalName && anyerr.originalName === "NotAllowedError") {
        setErrMsg("Please, enable access to your camera and microphone to proceed");
      } else {
        setErrMsg((err as Error).message);
      }
    });

    return function () {
      closing = true;
      pub?.destroy();
    };
  }, []);

  React.useEffect(function () {
    if (!props.groupMembership) { return; }
    groupServiceFactory().getGroupMembers(
        props.groupMembership.accountID, props.groupMembership.groupID).then(groupMembers => {
          const userIDSet = new Set<string>();
          for (const member of groupMembers) {
            userIDSet.add(member.userID);
          }
          const membership = authServiceFactory().currentAccountMembership;
          if (membership) {
            userIDSet.delete(membership.userID);
          }
          setGroupMemberSet(userIDSet);
        }, err => {
          console.error("Error getting group members:", err);
        });
  }, [props.groupMembership]);

  function findGroupMembers(searchVal: string) {
    if (!props.groupMembership) { return; }
    const authService = authServiceFactory();
    authService.getAccountMembersForGroup(
        props.groupMembership.groupID, searchVal, 100).then(mems => {
      mems = mems.filter(m => groupMemberSet.has(m.userID));
      mems.sort((a, b): number => {
        const res = a.lastname.localeCompare(b.lastname);
        if  (res != 0) { return res; }
        return a.firstname.localeCompare(b.firstname);
      });
      setMembers(mems);
    }, err => {
      console.error("Error fetching members:", err);
    });
  }
  React.useEffect(function () {
    if (timer) { clearTimeout(timer); }
    setTimer(setTimeout(function () {
      findGroupMembers(memberSearchVal);
    }, 500));
  }, [memberSearchVal]);

  function focusedSearch() {
    findGroupMembers("");
  }

  async function selectedOption (_event: React.SyntheticEvent, values: readonly Membership[] | null) {
    if (!values) { return; }
    setSelectedMembers(Array.from(values));
  }

  function changedVideo(event: SelectChangeEvent) {
    setVideoSource(event.target.value);
  }
  function changedAudio(event: SelectChangeEvent) {
    setAudioSource(event.target.value);
  }

  let classNames = "welcome-frame";
  if (!props.hideSessionOptions) {
    classNames += " new-session";
  }
  if (props.isWaiting) {
    classNames += " waitingForHost";
  }
  return <div className="welcome-window-container">
    <section className={classNames}>
      <div className="publisher-wrapper">
        <div id="publisher" />
        <div className="video media-source-container">
          <FormControl fullWidth size="small">
            <InputLabel id="video-src-label">Video Source:</InputLabel>
            <Select size="small" variant="outlined"
                labelId="video-src-label"
                label="Video Source:"
                value={videoSrcID}
                onChange={changedVideo}>
              {videoDevices.map(d => <MenuItem key={d.deviceId} value={d.deviceId}>{d.label}</MenuItem>)}
            </Select>
          </FormControl>
        </div>
        <div className="audio media-source-container">
          <FormControl fullWidth size="small">
            <InputLabel id="audio-src-label">Audio Source:</InputLabel>
            <Select size="small" variant="outlined"
                labelId="audio-src-label"
                label="Audio Source:"
                value={audioSrcID}
                onChange={changedAudio}>
              {audioDevices.map(d => <MenuItem key={d.deviceId} value={d.deviceId}>{d.label}</MenuItem>)}
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="session-form-container">
        {props.groupMembership && !isDM &&
          <div className="member-search-container">
            <FormLabel component="legend">Members to Call:</FormLabel>
            <Autocomplete id="account-member-search"
              multiple
              fullWidth
              size="small"
              options={members}
              getOptionLabel={(member: Membership) => `${member.firstname} ${member.lastname}`}
              onChange={selectedOption}
              onFocus={focusedSearch}
              onInputChange={(_event, newInputValue) => {
                setMemberSearchVal(newInputValue);
              }}
              renderInput={(params) =>
                <TextField variant="outlined" color="secondary" {...params} placeholder="To:" />} />
          </div>}
        <SessionsOptionsChooser isRouted={isRouted} setIsRouted={setIsRouted} />
        <div className="waiting-room">
          <div>{props.meetingHasStarted ? "We've notified the host you are ready..." : "Waiting for host..."}</div>
          <Button size="small" color="error" variant="contained" onClick={props.onClose}>
            <Tooltip title="Leave" arrow>
              <CloseIcon />
            </Tooltip>
          </Button>
        </div>
        <div className="room-controls center-button-row">
          {props.onClose && <Button size="small" color="error" variant="contained" onClick={props.onClose}>
            <Tooltip title="Close" arrow>
              <CloseIcon />
            </Tooltip>
          </Button>}
          <IconButton onClick={props.onToggleVideo} size="medium"
              disableRipple disableTouchRipple>
            <Tooltip title={props.sendVideo ? "Disable Video" : "Enable Video"} arrow>
              {props.sendVideo ? <VideocamIcon /> : <VideocamOffIcon />}
            </Tooltip>
          </IconButton>
          <IconButton onClick={props.onToggleAudio} size="medium"
              disableRipple disableTouchRipple>
            <Tooltip title={props.sendAudio ? "Disable Audio" : "Enable Audio"} arrow>
              {props.sendAudio ? <MicIcon /> : <MicOffIcon /> }
            </Tooltip>
          </IconButton>
          <Button size="small" color="success" variant="contained"
              disabled={!publisher}
              disableRipple disableTouchRipple
              onClick={() => props.onJoin(isRouted, isDM ? undefined : selectedMembers, videoSrcID, audioSrcID)}>
            {props.proceedText || "Join"}
          </Button>
        </div>
      </div>
      <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
    </section>
  </div>;
}

interface SessionOptionsProps {
  isRouted: boolean,
  setIsRouted(isRouted: boolean): void
}
export function SessionsOptionsChooser(props: SessionOptionsProps): React.ReactElement<any, any> {
  return <div className="session-options">
    <FormControl component="fieldset" className="session-type-options">
      <FormLabel component="legend">Session Type:</FormLabel>
    <RadioGroup name="routing-type"
        value={props.isRouted}
        onChange={evt => props.setIsRouted(evt.target.value === "true")}>
      <FormControlLabel value="true" control={<Radio color="secondary" />} label={<div>
            <h5 className="title">Performance Optimized</h5>
            <span className="desc">
              Optimized for larger huddles, users with limited bandwidth,
              and devices with limited resources.</span>
          </div>

      }/>
      <FormControlLabel value="false" control={<Radio color="secondary" />} label={<div>
            <h5 className="title">Ultra-Secure</h5>
            <span className="desc">Enables additional security measures.
            Some users may experience performance issues on devices with
            limited resources.</span>
          </div>}/>
    </RadioGroup>
    </FormControl>
  </div>;
}
